import { Link } from "react-router-dom";
import {
  eventMonthAbbrev,
  eventDayOfMonth,
  formatTimeRange,
} from "../utils/eventFormat";

/**
 * EventRow — shared "event-feed row" used by the public Events feed and the
 * Home schedule (SPR-009 P2-1). Mirrors the mobile prototype's schedule stop:
 * a teal day-chip (month + day) on the left, then the event title with a
 * time · place line beneath it. One component → web Events and Home are
 * visually identical.
 *
 * Wrapped in a Link to the event detail page when `event.id` is present.
 *
 * @param {{ event: object }} props
 */
export default function EventRow({ event }) {
  const place = event.locationAddress;
  const time = formatTimeRange(event.startTime, event.endTime);
  // Time · place line — join with a middot only when both are present.
  const meta = [time, place].filter(Boolean).join(" · ");

  const inner = (
    <div className="flex items-stretch gap-4">
      {/* Teal day-chip */}
      <div
        className="flex flex-col items-center justify-center shrink-0 w-14 rounded-xl bg-teal text-white px-2 py-2"
        data-testid={`event-chip-${event.id}`}
      >
        <span className="font-display text-sm leading-none uppercase">
          {eventMonthAbbrev(event.eventDate)}
        </span>
        <span className="font-heading text-xl leading-none mt-1">
          {eventDayOfMonth(event.eventDate)}
        </span>
      </div>

      {/* Title + time · place */}
      <div className="min-w-0 flex-1">
        <h3 className="font-display text-xl text-charcoal uppercase leading-tight">
          {event.title}
        </h3>
        <p className="font-body text-ink/60 text-sm mt-1">{meta}</p>
      </div>
    </div>
  );

  if (event.id == null) {
    return (
      <div
        className="block bg-white border-2 border-charcoal rounded-2xl p-4 shadow-[4px_4px_0_rgba(43,39,36,0.12)]"
        data-testid={`event-card-${event.id}`}
      >
        {inner}
      </div>
    );
  }

  return (
    <Link
      to={`/events/${event.id}`}
      className="block bg-white border-2 border-charcoal rounded-2xl p-4 shadow-[4px_4px_0_rgba(43,39,36,0.12)] hover:-translate-y-0.5 transition-transform"
      data-testid={`event-card-${event.id}`}
    >
      {inner}
    </Link>
  );
}
